import React, { FC, useState, useEffect, useCallback } from 'react';
import { useHistory } from 'react-router-dom';
import { up } from 'styled-breakpoints';
import { useBreakpoint } from 'styled-breakpoints/react-styled';
import Spacer from '@fenderdigital/ui/Spacer';
import BodyText from '@fenderdigital/ui/BodyText';
import HorizontalRule from '@fenderdigital/ui/HorizontalRule';
import usePlayUser from '@fenderdigital/custom-hooks/hooks/usePlayUser';
import ScreenReaderInstructions from '../../../SearchPanel/SearchPanelScreenReaderInstructions';
import { getInstrumentFacetFilter } from '../../../../utils/search-utils';
import NoResults from '../NoResults';
import Link from '../../../Link';
import { SearchResultsProps, Results } from '../SearchResults';
import useSelectSearchOption from '../../../../hooks/useSelectSearchOption';
import { useSearchSelection } from '../../../../contexts/SearchSelectionContext';
import FilterButton from './CategoryButton';
import CategoryResults from '../CategoryResults';
import * as S from './SearchResultsContent-styled';

type Props = {
  children: React.ReactNode;
  waitBeforeShow?: number;
};

interface SearchResultsContentProps extends SearchResultsProps {
  totalResults: number;
}

const Delayed: FC<Props> = ({ children, waitBeforeShow = 1000 }) => {
  const [isShown, setIsShown] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => {
      setIsShown(true);
    }, waitBeforeShow);
    return (): void => clearTimeout(timer);
  }, [waitBeforeShow]);

  return isShown ? <>{children}</> : null;
};

const SearchResultsContent: FC<SearchResultsContentProps> = ({
  instrument,
  facets,
  query,
  onCategoryChange,
  results,
  totalResults,
  onClose,
}) => {
  const { user } = usePlayUser();
  const isTabletUp = useBreakpoint(up('tablet-portrait'));
  const instruments = getInstrumentFacetFilter(instrument, facets?.instrument, query);
  const { resetOptions } = useSelectSearchOption('#nav-search-results [role="option"]');
  const { selectedOptionId } = useSearchSelection();
  const history = useHistory();

  const limitResults = (list: Results[], title: string): Results[] => {
    if (isTabletUp) {
      return list.slice(0, 3);
    }
    return title === 'Collections' ? list.slice(0, 3) : list.slice(0, 6);
  };

  const hasMoreResults = (list: Results[], title: string): boolean => {
    if (title === 'Collections') return list.length > 3;
    return list.length > 6;
  };

  const handleEnter = useCallback((): void => {
    const idToUrlParser = (): string => {
      const stringList = selectedOptionId?.split('-');
      if (stringList) {
        const type = stringList[0];
        const slug = stringList.slice(1).join('-');

        if (type === 'collection') return `/${instrument}/collections/${slug}`;
        if (type === 'artist')
          return `/search/results?query=${query}&instrument=guitar&artist=${encodeURI(slug)}`;
        return `/${type}/${slug}`;
      }
      return '/';
    };

    const url = idToUrlParser();
    history.push(url);
    onClose();
  }, [history, instrument, onClose, query, selectedOptionId]);

  useEffect(() => {
    resetOptions();
  }, [query, resetOptions, isTabletUp]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent): void => {
      if (selectedOptionId && e.key === 'Enter') {
        e.preventDefault();
        handleEnter();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return (): void => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [selectedOptionId, handleEnter]);

  if (!results) {
    return (
      <S.ErrorContainer>
        <Spacer spacing="lg3" />
        <S.ErrorIcon aria-hidden="true" />
        <Spacer spacing="lg1" />
        <BodyText textSize="f6" fontFamily="futuraDemi" textColor="midGray">
          Something went wrong
        </BodyText>
        <Spacer spacing="sm3" />
        <BodyText textSize="f3" textColor="gray">
          Please try your search again
        </BodyText>
        <Spacer spacing="lg3" />
        <BodyText textSize="f2" textColor="gray">
          You can also try visiting our{' '}
          <Link
            to="/search?query="
            css={`
              color: black;
              text-decoration: underline;
              &:visited {
                color: black;
              }
            `}
          >
            Search{' '}
          </Link>{' '}
          Page.
        </BodyText>
      </S.ErrorContainer>
    );
  }

  if (!totalResults) return <NoResults onClose={onClose} query={query} />;

  return (
    <S.ResultsContainer>
      <ScreenReaderInstructions aria-live="assertive">
        <Delayed>{`${totalResults} results found`}</Delayed>
      </ScreenReaderInstructions>
      <BodyText
        fontFamily="futuraDemi"
        textSize="f6"
        css={`
          line-height: 1;
        `}
      >
        Top Results
      </BodyText>
      <Spacer spacing="md3" />
      <S.SectionRow>
        <S.FilterIcon />
        {instruments?.items?.map((category: any) => (
          <FilterButton
            key={category.id}
            buttonClick={(): void => onCategoryChange(category.id)}
            category={category}
            instrument={instrument}
          />
        ))}
      </S.SectionRow>
      <Spacer spacing="sm2" />
      <ul role="listbox" id="nav-search-results">
        {results?.map(result => {
          if (result.items.length) {
            const topResults = limitResults(result.items, result.title);
            const resultsWithFcId = topResults.map(data => {
              data.trackEvent.properties.fc_id = user?.userID ?? '';
              return data;
            });

            return (
              <li key={result.title} role="group">
                <CategoryResults
                  results={resultsWithFcId}
                  type={result.title}
                  onClick={onClose}
                  selectedOptionId={selectedOptionId}
                />
                {!isTabletUp &&
                  hasMoreResults(result.items, result.title) &&
                  result.title !== 'Artists' && (
                    <>
                      <S.MoreResultsButton to={`${result?.url}`} onClick={onClose}>
                        <BodyText
                          fontFamily="futuraDemi"
                          css={`
                            line-height: 1;
                          `}
                        >
                          {`See more ${result.title.toLowerCase()}`}
                        </BodyText>
                      </S.MoreResultsButton>
                      <Spacer spacing="md4" />
                    </>
                  )}
                <HorizontalRule ariaHidden />
              </li>
            );
          }
          return null;
        })}
      </ul>
    </S.ResultsContainer>
  );
};

export default SearchResultsContent;
